import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { apiErrorMessage, stripePaymentsService } from '../../services/api';
import { StripeConnectionStatus } from '../../types';
import { StatusChip } from '../ui';

interface Props {
  shopId?: string;
  onNotice: (message: string) => void;
}

const EMPTY = { secretKey: '', publishableKey: '', webhookSecret: '', readerId: '' };

/**
 * Stripe for card payments — the pay page and a Stripe Terminal reader. Keys
 * are verified with Stripe before they are stored, and never shown again.
 */
const StripeConnectionCard: React.FC<Props> = ({ shopId, onNotice }) => {
  const [status, setStatus] = useState<StripeConnectionStatus | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [primary, setPrimary] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await stripePaymentsService.getStatus(shopId);
      setStatus(next);
      setForm((current) => ({ ...current, readerId: next.readerId ?? '' }));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load the Stripe connection.'));
    }
  }, [shopId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (action: () => Promise<StripeConnectionStatus | void>, message: string) => {
    setBusy(true);
    setError(null);
    try {
      const next = await action();
      if (next) setStatus(next);
      else await load();
      onNotice(message);
    } catch (err) {
      setError(apiErrorMessage(err, 'Stripe could not be updated.'));
    } finally {
      setBusy(false);
    }
  };

  const field =
    (key: keyof typeof EMPTY) => (event: React.ChangeEvent<HTMLInputElement>) =>
      setForm((current) => ({ ...current, [key]: event.target.value }));

  const connect = () =>
    run(async () => {
      const next = await stripePaymentsService.connect(
        {
          secretKey: form.secretKey.trim(),
          publishableKey: form.publishableKey.trim(),
          webhookSecret: form.webhookSecret.trim() || undefined,
          readerId: form.readerId.trim() || undefined,
          primary,
        },
        shopId,
      );
      setForm({ ...EMPTY, readerId: next.readerId ?? '' });
      return next;
    }, 'Stripe connected');

  const copyWebhookUrl = async () => {
    if (!status) return;
    try {
      await navigator.clipboard.writeText(status.webhookUrl);
      onNotice('Webhook URL copied');
    } catch {
      onNotice('Copy failed — select the URL and copy it by hand');
    }
  };

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, p: 3 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Stripe
          </Typography>
          {status?.connected ? (
            <>
              <StatusChip status="active" label="Connected" />
              <Chip
                size="small"
                variant="outlined"
                label={status.mode === 'live' ? 'Live' : 'Test mode'}
              />
              {status.primary && <Chip size="small" variant="outlined" label="Takes card payments" />}
            </>
          ) : (
            <StatusChip status="disconnected" label="Not connected" />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          Take card payments on the invoice pay page and on a Stripe Terminal reader. Money goes
          straight to your own Stripe account.
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {status && !status.allowed && (
          <Alert severity="warning">
            Stripe is turned off for this shop by the platform administrator. No new payments go
            through it; payments it already took can still be refunded.
            {status.connected ? ' Your keys are kept for when it is turned back on.' : ''}
          </Alert>
        )}

        {status && !status.allowed && status.connected && (
          <Box>
            {/* Removing your own keys is always allowed. */}
            <Button
              color="error"
              disabled={busy}
              onClick={() => run(() => stripePaymentsService.disconnect(shopId), 'Stripe disconnected')}
            >
              Disconnect Stripe
            </Button>
          </Box>
        )}

        {status && status.allowed && !status.connected && (
          <Stack spacing={2}>
            <TextField
              label="Secret or restricted key"
              type="password"
              autoComplete="off"
              value={form.secretKey}
              onChange={field('secretKey')}
              helperText="Stripe Dashboard → Developers → API keys. A restricted key (rk_…) with PaymentIntents, Refunds and Terminal write access is safest."
            />
            <TextField
              label="Publishable key"
              value={form.publishableKey}
              onChange={field('publishableKey')}
              helperText="Starts with pk_ — the pay page uses it to show the card form."
            />
            <TextField
              label="Webhook signing secret (optional now)"
              type="password"
              autoComplete="off"
              value={form.webhookSecret}
              onChange={field('webhookSecret')}
              helperText={`Add a webhook endpoint in Stripe for ${status.webhookUrl}, then paste its whsec_… secret.`}
            />
            <TextField
              label="Terminal reader ID (optional)"
              value={form.readerId}
              onChange={field('readerId')}
              helperText="tmr_… — for card-present payments from the invoice screen."
            />
            <FormControlLabel
              control={<Switch checked={primary} onChange={(e) => setPrimary(e.target.checked)} />}
              label="Use Stripe for card payments (instead of Square)"
            />
            <Box>
              <Button
                variant="contained"
                disabled={busy || !form.secretKey.trim() || !form.publishableKey.trim()}
                onClick={connect}
              >
                {busy ? 'Checking keys…' : 'Connect Stripe'}
              </Button>
            </Box>
          </Stack>
        )}

        {status?.connected && status.allowed && (
          <Stack spacing={2}>
            <Typography variant="body2">
              {status.accountName || 'Stripe account'}{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                ({status.accountId})
              </Typography>
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={status.primary}
                  disabled={busy}
                  onChange={(e) =>
                    run(
                      () =>
                        stripePaymentsService.updateSettings({ primary: e.target.checked }, shopId),
                      e.target.checked
                        ? 'Stripe now takes card payments'
                        : 'Stripe kept connected for refunds only',
                    )
                  }
                />
              }
              label="Use Stripe for new card payments"
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <TextField
                size="small"
                label="Terminal reader ID"
                value={form.readerId}
                onChange={field('readerId')}
                placeholder="tmr_…"
                sx={{ flex: 1 }}
              />
              <Button
                disabled={busy || form.readerId.trim() === (status.readerId ?? '')}
                onClick={() =>
                  run(
                    () =>
                      stripePaymentsService.updateSettings(
                        { readerId: form.readerId.trim() },
                        shopId,
                      ),
                    'Reader saved',
                  )
                }
              >
                Save reader
              </Button>
            </Stack>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Webhook
              </Typography>
              {!status.webhookConfigured && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  Payments that finish after the customer closes the page (bank confirmations,
                  terminal payments) need the webhook.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary">
                Endpoint URL to add in Stripe (events: {status.requiredEvents.join(', ')}):
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                >
                  {status.webhookUrl}
                </Typography>
                <Button size="small" onClick={copyWebhookUrl}>
                  Copy
                </Button>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                <TextField
                  size="small"
                  type="password"
                  autoComplete="off"
                  label={status.webhookConfigured ? 'Replace signing secret' : 'Signing secret'}
                  value={form.webhookSecret}
                  onChange={field('webhookSecret')}
                  placeholder="whsec_…"
                  sx={{ flex: 1 }}
                />
                <Button
                  disabled={busy || !form.webhookSecret.trim()}
                  onClick={() =>
                    run(async () => {
                      const next = await stripePaymentsService.updateSettings(
                        { webhookSecret: form.webhookSecret.trim() },
                        shopId,
                      );
                      setForm((current) => ({ ...current, webhookSecret: '' }));
                      return next;
                    }, 'Webhook secret saved')
                  }
                >
                  Save secret
                </Button>
              </Stack>
            </Box>

            <Box>
              <Button
                color="error"
                disabled={busy}
                onClick={() =>
                  run(() => stripePaymentsService.disconnect(shopId), 'Stripe disconnected')
                }
              >
                Disconnect Stripe
              </Button>
            </Box>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default StripeConnectionCard;
